import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const CATEGORIES = ['Screen guard', 'Camera ring', 'Charger cable', 'Airpods'];

const PHONE_MODELS = [
  // iPhone 17 Series
  'iPhone 17 air',
  'iPhone 17 pro max',
  'iPhone 17 pro',
  'iPhone 17 plus',
  'iPhone 17',
  // iPhone 16 Series
  'iPhone 16 pro max',
  'iPhone 16 pro',
  'iPhone 16 plus',
  'iPhone 16',
  // iPhone 15 Series
  'iPhone 15 pro max',
  'iPhone 15 pro',
  'iPhone 15 plus',
  'iPhone 15',
  // iPhone 14 Series
  'iPhone 14 pro max',
  'iPhone 14 pro',
  'iPhone 14 Plus',
  'iPhone 14',
  // iPhone 13 Series
  'iPhone 13 pro',
  'iPhone 13 plus',
  'iPhone 13',
];

const CABLE_TYPES = [
  'Digital display cable C to C',
  'Digital display cable C to Lightning',
  'Digital display cable USB to C',
  'Silicon cable USB to C',
  'Silicon cable C to C',
  'Silicon cable C to Lightning',
  'Silicon cable USB to Lightning',
  'Rope cable C to C',
  'Rope cable USB to C',
  'Rope cable C to Lightning',
  '4 in 1 cable',
];

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: product?.title || '',
    modelName: product?.modelName || '',
    category: product?.category || '',
    description: product?.description || '',
    originalPrice: product?.originalPrice || '',
    discountPrice: product?.discountPrice || '',
    colors: product?.colors?.join(', ') || '',
    image1: product?.images?.[0] || '',
    image2: product?.images?.[1] || '',
    image3: product?.images?.[2] || '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({ img1: false, img2: false, img3: false });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (file, imageNumber) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload only JPG, PNG, or WEBP images');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploading(prev => ({ ...prev, [`img${imageNumber}`]: true }));

    try {
      const timestamp = Date.now();
      const filename = `products/${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      setFormData(prev => ({ ...prev, [`image${imageNumber}`]: downloadURL }));
      toast.success(`Image ${imageNumber} uploaded successfully!`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [`img${imageNumber}`]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.originalPrice || !formData.category || !formData.image1) {
      toast.error('Please fill in all required fields (title, price, category, at least 1 image)');
      return;
    }

    setLoading(true);

    try {
      const images = [formData.image1, formData.image2, formData.image3].filter(img => img.trim());
      const colors = formData.colors.split(',').map(c => c.trim()).filter(c => c);
      
      const productData = {
        title: formData.title,
        modelName: formData.modelName,
        category: formData.category,
        description: formData.description,
        originalPrice: parseFloat(formData.originalPrice),
        discountPrice: formData.discountPrice ? parseFloat(formData.discountPrice) : null,
        price: formData.discountPrice ? parseFloat(formData.discountPrice) : parseFloat(formData.originalPrice),
        colors: colors,
        images: images,
      };

      await onSave(productData);
      toast.success(product ? 'Product updated!' : 'Product created!');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-brand-navy">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <Label htmlFor="title">Product Name *</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Premium Tempered Glass Screen Protector"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category *</Label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                required
              >
                <option value="">Select Category</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {formData.category === 'Charger cable' ? (
              <div>
                <Label htmlFor="modelName">Cable Type *</Label>
                <select
                  id="modelName"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  required
                >
                  <option value="">Select Cable Type</option>
                  {CABLE_TYPES.map(cable => (
                    <option key={cable} value={cable}>{cable}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <Label htmlFor="modelName">Phone Model *</Label>
                <select
                  id="modelName"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  required
                >
                  <option value="">Select Model</option>
                  {PHONE_MODELS.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="originalPrice">Original Price (₹) *</Label>
              <Input
                id="originalPrice"
                name="originalPrice"
                type="number"
                step="0.01"
                value={formData.originalPrice}
                onChange={handleChange}
                placeholder="1999"
                required
              />
            </div>

            <div>
              <Label htmlFor="discountPrice">Discount Price (₹)</Label>
              <Input
                id="discountPrice"
                name="discountPrice"
                type="number"
                step="0.01"
                value={formData.discountPrice}
                onChange={handleChange}
                placeholder="999"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty if no discount</p>
            </div>
          </div>

          <div>
            <Label htmlFor="colors">Available Colors</Label>
            <Input
              id="colors"
              name="colors"
              value={formData.colors}
              onChange={handleChange}
              placeholder="e.g., Black, White, Blue (comma separated)"
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple colors with commas</p>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-brand-blue"
              placeholder="Detailed product description..."
            />
          </div>

          <div className="space-y-4">
            <Label>Product Images (Min 1, Max 3) *</Label>
            
            <div>
              <Label htmlFor="image1" className="text-sm">Image 1 (Required) *</Label>
              <div className="flex gap-2">
                <Input
                  id="image1"
                  name="image1"
                  value={formData.image1}
                  onChange={handleChange}
                  placeholder="https://example.com/image1.jpg"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file1').click()}
                  disabled={uploading.img1}
                >
                  {uploading.img1 ? 'Uploading...' : <Upload className="w-4 h-4" />}
                </Button>
                <input
                  id="file1"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 1)}
                />
              </div>
              {formData.image1 && (
                <img
                  src={formData.image1}
                  alt="Preview 1"
                  className="mt-2 w-24 h-24 object-cover rounded-lg border"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            <div>
              <Label htmlFor="image2" className="text-sm">Image 2 (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="image2"
                  name="image2"
                  value={formData.image2}
                  onChange={handleChange}
                  placeholder="https://example.com/image2.jpg"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file2').click()}
                  disabled={uploading.img2}
                >
                  {uploading.img2 ? 'Uploading...' : <Upload className="w-4 h-4" />}
                </Button>
                <input
                  id="file2"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 2)}
                />
              </div>
              {formData.image2 && (
                <img
                  src={formData.image2}
                  alt="Preview 2"
                  className="mt-2 w-24 h-24 object-cover rounded-lg border"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>

            <div>
              <Label htmlFor="image3" className="text-sm">Image 3 (Optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="image3"
                  name="image3"
                  value={formData.image3}
                  onChange={handleChange}
                  placeholder="https://example.com/image3.jpg"
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file3').click()}
                  disabled={uploading.img3}
                >
                  {uploading.img3 ? 'Uploading...' : <Upload className="w-4 h-4" />}
                </Button>
                <input
                  id="file3"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0], 3)}
                />
              </div>
              {formData.image3 && (
                <img
                  src={formData.image3}
                  alt="Preview 3"
                  className="mt-2 w-24 h-24 object-cover rounded-lg border"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              type="submit"
              className="flex-1 bg-brand-blue hover:bg-brand-navy"
              disabled={loading}
            >
              {loading ? 'Saving...' : (product ? 'Update Product' : 'Add Product')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
